import { PageLoader } from '@/shared/ui';
import { Outlet } from 'react-router-dom';
import { useEffect } from 'react';

import { AboutModal } from '@/features/about-modal';
import { PreferencesModal } from '@/features/preferences-modal';
import { ChangePasswordModal } from '@/features/change-password-modal';
import { useGlobalMenuHotkeys } from '@/features/user-menu';
import { HeaderFiltersProvider, ActiveFiltersBar } from '@/features/header-filters';

import { ModalKeys } from '@/shared/config/modals';
import { useModalParams } from '@/shared/lib/hooks';
import { useAppDispatch } from '@/shared/lib';

import { Sidebar } from '@/widgets/sidebar';
import { useGetUserContextQuery } from '@/entities/layout';
import { setLayoutType, LAYOUT_TYPES } from '@/entities/language';
import { useIsMobile } from '@/hooks/use-mobile';

import { Header } from './header';
import { MobileHeader } from './mobile-header';

export const PrivateLayout = () => {
  const { isLoading: isLayoutLoading, error } = useGetUserContextQuery();
  const isMobile = useIsMobile();
  const aboutModal = useModalParams(ModalKeys.ABOUT);
  const preferencesModal = useModalParams(ModalKeys.PREFERENCES);
  const changePasswordModal = useModalParams(ModalKeys.CHANGE_PASSWORD);
  const dispatch = useAppDispatch();

  useGlobalMenuHotkeys();

  useEffect(() => {
    dispatch(setLayoutType(LAYOUT_TYPES.PRIVATE));
  }, [dispatch]);

  if (isLayoutLoading) {
    return <PageLoader messageKey="common.loadingApp" />;
  }

  if (error) {
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <div className="text-center text-destructive">
          <p>Failed to load application data</p>
          <p className="text-sm text-muted-foreground mt-1">Please try refreshing the page</p>
        </div>
      </div>
    );
  }

  return (
    <HeaderFiltersProvider>
      <div className="flex h-screen w-full overflow-hidden bg-background">
        <Sidebar />
        <div className="flex flex-col flex-1 overflow-hidden">
          {isMobile ? <MobileHeader /> : <Header />}
          <div className="flex flex-1 overflow-hidden">
            <main className="flex-1 overflow-y-auto bg-secondary/10 p-4 md:p-6">
              <div className="flex min-h-full flex-col">
                <ActiveFiltersBar className="mb-4" />
                <Outlet />
              </div>
            </main>
          </div>
        </div>
      </div>
      <AboutModal open={aboutModal.isOpen} onOpenChange={aboutModal.close} />
      <PreferencesModal open={preferencesModal.isOpen} onOpenChange={preferencesModal.close} />
      <ChangePasswordModal open={changePasswordModal.isOpen} onOpenChange={changePasswordModal.close} />
    </HeaderFiltersProvider>
  );
};
